import React, { useEffect, useState, Fragment, useCallback, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import Button from '../../shared/TailwindComponents/Button';
import { PiSidebarLight, PiTextBLight, PiTextItalicLight, PiTextUnderlineLight, PiListBulletsLight, PiListNumbersLight, PiCaretDownLight } from 'react-icons/pi';
import { Menu, Transition } from '@headlessui/react';
import _ from 'lodash';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import Badge from '../../shared/TailwindComponents/Badge';
import Dropdown from '../../shared/TailwindComponents/Dropdown';
import './signature.css'
import { formatInsightName } from '../../../utils/functions';

interface DynamicEditorProps {
  editorRef: any;
  contentType: any | 'caseAnalysis';
  matterId: string;
  content: string;
  onSave: (content: string) => void;
  isFullscreen: boolean;
  setIsFullscreen: (val: boolean) => void;
  cursors?: any;
  setCursors?: any
  handleMarkAsFinal?: any;
  handleDownload?: any;
  selectedVersionStatus?: any;
  showDownloadOptions?: any;
  setShowDownloadOptions?: any;
  downloadFormat?: any;
  setDownloadFormat?: any;
  includeBranding?: any;
  setIncludeBranding?: any;
  brandedTemplates?: any[];
  selectedBrandedTemplateId?: string | null;
  setSelectedBrandedTemplateId?: (id: string | null) => void;
  versionOptions?: any;
  handleVersionChange?: any;
  selectedVersion?: any
  isReadOnly?: boolean;
  isClient?: boolean;
  onAccept?: () => void;
  onDecline?: (comment: string) => void;
  clientResponse?: string;
  fromlead?: boolean;
}

interface OutlineNode {
  level: number;
  text: string;
  id: string;
  children: OutlineNode[];
}

interface TypingIndicator {
  name: string;
  position: number;
}

const getColorForUser = (userId: string) => {
  const colors = ['#e6194b', '#3cb44b', '#ffe119', '#4363d8', '#f58231', '#911eb4'];
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = userId.charCodeAt(i) + ((hash << 5) - hash);
  }
  return colors[Math.abs(hash) % colors.length];
};

const renderCollaboratorCursors = (editorRef: React.RefObject<HTMLDivElement>, typingIndicators: Record<string, TypingIndicator>) => {
  if (!editorRef.current) return;
  const oldMarkers = editorRef.current.querySelectorAll('.collab-cursor');
  oldMarkers.forEach(marker => marker.remove());

  Object.entries(typingIndicators).forEach(([userId, { name, position }]) => {
    const range = document.createRange();
    const sel = window.getSelection();
    if (!sel || !editorRef.current?.firstChild) return;

    let node = editorRef.current.firstChild as Node;
    if (node.nodeType === Node.TEXT_NODE && node.textContent) {
      const offset = Math.min(position, node.textContent.length);
      range.setStart(node, offset);
      range.collapse(true);

      const cursorEl = document.createElement('span');
      cursorEl.className = 'collab-cursor';
      cursorEl.style.borderLeft = `2px solid ${getColorForUser(userId)}`;
      cursorEl.style.marginLeft = '-1px';
      cursorEl.style.height = '1em';
      cursorEl.style.display = 'inline-block';
      cursorEl.style.position = 'relative';
      cursorEl.style.zIndex = '10';

      const label = document.createElement('span');
      label.textContent = name;
      label.style.position = 'absolute';
      label.style.top = '-1.5em';
      label.style.left = '0';
      label.style.background = getColorForUser(userId);
      label.style.color = 'white';
      label.style.fontSize = '10px';
      label.style.padding = '1px 4px';
      label.style.borderRadius = '4px';
      label.style.whiteSpace = 'nowrap';
      label.style.zIndex = '100';

      cursorEl.appendChild(label);
      range.insertNode(cursorEl);
    }
  });
};

const DynamicEditor = forwardRef<HTMLDivElement, DynamicEditorProps>(({
  editorRef,
  contentType,
  matterId,
  content,
  onSave,
  isFullscreen,
  setIsFullscreen,
  cursors,
  setCursors,
  handleMarkAsFinal,
  handleDownload,
  selectedVersionStatus,
  showDownloadOptions,
  setShowDownloadOptions,
  downloadFormat,
  includeBranding,
  setIncludeBranding,
  brandedTemplates,
  selectedBrandedTemplateId,
  setSelectedBrandedTemplateId,
  setDownloadFormat,
  versionOptions,
  handleVersionChange,
  selectedVersion,
  isReadOnly,
  isClient,
  onAccept,
  onDecline,
  clientResponse,
  fromlead
}, ref) => {
  const { t } = useTranslation();
  const [showOutline, setShowOutline] = useState(false);
  const [history, setHistory] = useState<{ content: string; selection: Range | null }[]>([{ content, selection: null }]);
  const [historyIndex, setHistoryIndex] = useState<number>(0);
  const [currentBlockType, setCurrentBlockType] = useState<string>('Normal text');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [isUnderline, setIsUnderline] = useState(false);
  const [isUnorderedList, setIsUnorderedList] = useState(false);
  const [isOrderedList, setIsOrderedList] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const userInfo = useSelector((state: RootState) => state.user.userInfo)
  const [typingIndicators, setTypingIndicators] = useState<Record<string, TypingIndicator>>({});

  const handleFocus = () => {
    // Socket event removed - legacy code
  };

  const broadcastContentChange = _.debounce((html: string) => {
    // Socket event removed - legacy code
  }, 200);

  // Socket event listeners removed - legacy code

  useEffect(() => {
    renderCollaboratorCursors(editorRef, typingIndicators);
  }, [typingIndicators]);

  useEffect(() => {
    if (editorRef.current) editorRef.current.innerHTML = content;
  }, [content, isFullscreen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().includes('MAC');
      const modKey = isMac ? e.metaKey : e.ctrlKey;
      if (modKey) {
        switch (e.key.toLowerCase()) {
          case 'b': e.preventDefault(); applyFormat('bold'); break;
          case 'i': e.preventDefault(); applyFormat('italic'); break;
          case 'u': e.preventDefault(); applyFormat('underline'); break;
          case 'z': e.preventDefault(); undo(); break;
          case 'y': e.preventDefault(); redo(); break;
          case 's': e.preventDefault(); onSave(editorRef.current?.innerHTML || ''); break;
          case '1': case '2': case '3': case '4': case '5': case '6':
            e.preventDefault(); handleHeading(parseInt(e.key)); break;
        }
      } else if (e.key === 'Escape' && isFullscreen) {
        setIsFullscreen(false);
      }
    };

    const editor = editorRef.current;
    if (editor) {
      editor.addEventListener('keydown', handleKeyDown);
      editor.addEventListener('keyup', updateFormattingStates);
      editor.addEventListener('click', updateFormattingStates);
      document.addEventListener('selectionchange', updateFormattingStates);
    }
    return () => {
      if (editor) {
        editor.removeEventListener('keydown', handleKeyDown);
        editor.removeEventListener('keyup', updateFormattingStates);
        editor.removeEventListener('click', updateFormattingStates);
        document.removeEventListener('selectionchange', updateFormattingStates);
      }
    };
  }, [history, historyIndex, onSave, isFullscreen, setIsFullscreen]);

  const getBlockAncestor = (node: Node): HTMLElement | null => {
    let current: Node | null = node;
    while (current && current !== editorRef.current) {
      if (current.nodeType === 1) {
        const tag = (current as HTMLElement).tagName.toLowerCase();
        if (['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6'].includes(tag)) return current as HTMLElement;
      }
      current = current.parentNode;
    }
    return null;
  };

  const updateBlockType = useCallback(
    _.debounce(() => {
      if (!editorRef.current) return;
      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0) return;
      const anchorNode = selection.anchorNode;
      if (!anchorNode) return;
      const blockElement = getBlockAncestor(anchorNode);
      if (blockElement) {
        const tag = blockElement.tagName.toLowerCase();
        setCurrentBlockType(tag.startsWith('h') && tag.length === 2 ? `Header ${tag[1]}` : 'Normal text');
      } else {
        setCurrentBlockType('Normal text');
      }
    }, 100),
    []
  );

  const updateFormattingStates = useCallback(() => {
    setIsBold(document.queryCommandState('bold'));
    setIsItalic(document.queryCommandState('italic'));
    setIsUnderline(document.queryCommandState('underline'));
    setIsUnorderedList(document.queryCommandState('insertUnorderedList'));
    setIsOrderedList(document.queryCommandState('insertOrderedList'));
  }, []);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor) {
      editor.addEventListener('click', updateBlockType);
      editor.addEventListener('keyup', updateBlockType);
      document.addEventListener('selectionchange', updateBlockType);
    }
    return () => {
      if (editor) {
        editor.removeEventListener('click', updateBlockType);
        editor.removeEventListener('keyup', updateBlockType);
        document.removeEventListener('selectionchange', updateBlockType);
      }
    };
  }, [updateBlockType]);

  const saveSelection = (): Range | null => {
    const selection = window.getSelection();
    return selection && selection.rangeCount > 0 ? selection.getRangeAt(0).cloneRange() : null;
  };

  const restoreSelection = (range: Range | null) => {
    if (range) {
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  };

  const debouncedUpdateHistory = useCallback(
    _.debounce(() => {
      if (!editorRef.current) return;
      const content = editorRef.current.innerHTML;
      const selection = saveSelection();
      if (content !== history[historyIndex]?.content) {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push({ content, selection });
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
      }
    }, 300),
    [history, historyIndex]
  );

  const applyFormat = (command: string) => {
    if (editorRef.current) {
      const savedRange = saveSelection();
      editorRef.current.focus();
      document.execCommand(command, false, undefined);
      restoreSelection(savedRange);
      debouncedUpdateHistory();
    }
  };

  const handleHeading = (level: number | 'p') => {
    if (!editorRef.current) return;
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return;
    const range = selection.getRangeAt(0);
    const blockElement = getBlockAncestor(range.startContainer) || getBlockAncestor(range.commonAncestorContainer);
    if (!blockElement) return;

    const currentTag = blockElement.tagName.toLowerCase();
    const newTag = level === 'p' ? 'p' : `h${level}`;
    if (currentTag === newTag) return;

    const newElement = document.createElement(newTag);
    newElement.innerHTML = blockElement.innerHTML;
    if (blockElement.id) newElement.id = blockElement.id;
    blockElement.parentNode?.replaceChild(newElement, blockElement);

    const newRange = document.createRange();
    newRange.selectNodeContents(newElement);
    newRange.collapse(false);
    selection.removeAllRanges();
    selection.addRange(newRange);
    debouncedUpdateHistory();
  };

  const undo = () => {
    if (historyIndex <= 0 || !editorRef.current) return;
    setHistoryIndex(prev => prev - 1);
    const { content, selection } = history[historyIndex - 1];
    editorRef.current.innerHTML = content;
    restoreSelection(selection);
  };

  const redo = () => {
    if (historyIndex >= history.length - 1 || !editorRef.current) return;
    setHistoryIndex(prev => prev + 1);
    const { content, selection } = history[historyIndex + 1];
    editorRef.current.innerHTML = content;
    restoreSelection(selection);
  };

  const outline = (() => {
    const headings: { level: number; text: string; id: string }[] = [];
    if (editorRef.current) {
      const elements = editorRef.current.querySelectorAll('h1, h2, h3, h4, h5, h6');
      elements.forEach((el: any, index: number) => {
        const level = parseInt(el.tagName[1]);
        const text = el.textContent || '';
        const id = el.id || `heading-${index + 1}`;
        if (!el.id) el.id = id;
        headings.push({ level, text, id });
      });
    }
    return headings;
  })();

  const buildOutlineTree = (headings: { level: number; text: string; id: string }[]): OutlineNode[] => {
    const tree: OutlineNode[] = [];
    const stack: OutlineNode[] = [];

    headings.forEach((heading) => {
      const node: OutlineNode = { ...heading, children: [] };
      while (stack.length > 0 && stack[stack.length - 1].level >= heading.level) {
        stack.pop();
      }
      if (stack.length > 0) {
        stack[stack.length - 1].children.push(node);
      } else {
        tree.push(node);
      }
      stack.push(node);
    });

    return tree;
  };

  const getTextSizeClass = (level: number) => {
    switch (level) {
      case 1: return 'text-sm';
      case 2: return 'text-sm';
      case 3: return 'text-xs';
      default: return 'text-xs';
    }
  };

  const toggleExpand = (id: string) => {
    setExpandedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  interface OutlineItemProps {
    node: OutlineNode;
    depth: number;
    expandedIds: Set<string>;
    toggleExpand: (id: string) => void;
    handleOutlineClick: (id: string) => void;
  }

  const OutlineItem: React.FC<OutlineItemProps> = ({ node, depth, expandedIds, toggleExpand, handleOutlineClick }) => {
    const isExpanded = expandedIds.has(node.id);
    const hasChildren = node.children.length > 0;
    const textSizeClass = getTextSizeClass(node.level);

    return (
      <li className={`pl-${depth * 4}`}>
        <div
          className={`group flex items-center justify-between cursor-pointer rounded hover:bg-gray-100 dark:hover:bg-gray-700 p-1 ${textSizeClass}`}
          onClick={() => handleOutlineClick(node.id)}
        >
          <span className={hasChildren ? '' : ''}>{node.text}</span>
          {hasChildren && (
            <button
              className="mr-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 text-gray-500"
              onClick={(e) => {
                e.stopPropagation();
                toggleExpand(node.id);
              }}
            >
              <PiCaretDownLight
                className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
              />
            </button>
          )}
        </div>
        {hasChildren && isExpanded && (
          <ul className="space-y-1 mt-1">
            {node.children.map(child => (
              <OutlineItem
                key={child.id}
                node={child}
                depth={depth + 1}
                expandedIds={expandedIds}
                toggleExpand={toggleExpand}
                handleOutlineClick={handleOutlineClick}
              />
            ))}
          </ul>
        )}
      </li>
    );
  };

  const outlineTree = buildOutlineTree(outline);

  const handleOutlineClick = (id: string) => {
    const target = editorRef.current;
    if (target) {
      const element = target.querySelector(`#${id}`);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const renderOutline = (isFullScreen: boolean) => (
    <div className={`fixed w-64 ${isFullScreen ? 'h-screen' : ''} p-4 border-r border-gray-200 dark:border-gray-700`}>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold">{t('Outline')}</h3>
        <Button variant="outline" size="small" onClick={() => setShowOutline(false)} >
          <PiSidebarLight size={18} />
        </Button>
      </div>
      {outlineTree.length > 0 ? (
        <ul className={`space-y-1 overflow-y-auto ${isFullScreen ? 'h-[calc(100vh-8rem)]' : 'h-[calc(100vh-220px)]'}`}>
          {outlineTree.map(node => (
            <OutlineItem
              key={node.id}
              node={node}
              depth={0}
              expandedIds={expandedIds}
              toggleExpand={toggleExpand}
              handleOutlineClick={handleOutlineClick}
            />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-gray-500 dark:text-gray-400">{t('No headings found')}</p>
      )}
    </div>
  );

  const renderEditor = (isFullScreen: boolean) => (
    <div className={`flex-1 ${isFullScreen ? 'h-screen' : 'h-full'} flex flex-col`}>
      <div className="relative px-4 py-2 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {!showOutline && (
              <Button variant="outline" size="small" onClick={() => setShowOutline(true)} className="hover:bg-gray-100 dark:hover:bg-gray-700">
                <PiSidebarLight size={18} />
              </Button>
            )}
            <h2 className="text-2xl">{contentType === 'caseAnalysis' ? t('Case Analysis') : formatInsightName(contentType)}</h2>
            {!isClient && selectedVersionStatus === 'Final' && <Badge color="green" className="ml-2">{t('Final')}</Badge>}
          </div>
          <div className="flex gap-2">
            {isClient && selectedVersionStatus === 'Final' && clientResponse === 'Pending' && (
              <>
                <Button variant="primary" size="small" onClick={onAccept}>
                  {t('Accept')}
                </Button>
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => {
                    const comment = prompt(t('Please provide a reason for declining'));
                    if (comment) onDecline?.(comment);
                  }}
                >
                  {t('Decline')}
                </Button>
              </>
            )}
            {!isClient && !fromlead && handleMarkAsFinal && (
              <Button variant="primary" size="small" onClick={handleMarkAsFinal} disabled={selectedVersionStatus === 'Final'}>
                {t('Mark as Final')}
              </Button>
            )}
            {!fromlead && (
              <div className="relative">
                <Button
                  variant="outline"
                  size="small"
                  onClick={() => setShowDownloadOptions(!showDownloadOptions)}
                  tooltip={t('Download')}
                >
                  {t('Download')}
                </Button>
                {showDownloadOptions && (
                  <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-md shadow-lg z-10 p-3">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">{t('Format')}</label>
                    <select
                      value={downloadFormat}
                      onChange={(e) => setDownloadFormat(e.target.value as 'docx' | 'pdf')}
                      className="mt-1 block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                    >
                      <option value="docx">DOCX</option>
                    </select>
                    {selectedVersionStatus === 'Final' && (
                      <>
                        <div className="mt-3">
                          <label className="flex items-center text-sm font-medium text-gray-700 dark:text-gray-300">
                            <input
                              type="checkbox"
                              checked={includeBranding}
                              onChange={(e) => setIncludeBranding(e.target.checked)}
                              className="mr-2"
                            />
                            {t('Apply Firm Branding')}
                          </label>
                        </div>
                        {includeBranding && brandedTemplates && brandedTemplates.length > 0 && (
                          <div className="mt-2">
                            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                              {t('Branded Template')}
                            </label>
                            <select
                              value={selectedBrandedTemplateId || ''}
                              onChange={(e) => setSelectedBrandedTemplateId?.(e.target.value || null)}
                              className="block w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-sm"
                            >
                              <option value="">{t('No template (use default branding)')}</option>
                              {brandedTemplates.map((template) => (
                                <option key={template.id} value={template.id}>
                                  {template.name} {template.isDefault ? '(Default)' : ''}
                                </option>
                              ))}
                            </select>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              {t('Select a template to apply its fonts, headers, and footers')}
                            </p>
                          </div>
                        )}
                        {includeBranding && (!brandedTemplates || brandedTemplates.length === 0) && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                            {t('Upload branded templates in Settings → Templates to customize document styling')}
                          </p>
                        )}
                      </>
                    )}
                    <Button variant="primary" size="small" onClick={handleDownload} className="mt-3 w-full">
                      {t('Download')}
                    </Button>
                  </div>
                )}
              </div>
            )}
            <Button variant="outline" tooltip={isFullScreen ? 'Shrink' : 'Expand'} size="small" onClick={() => setIsFullscreen(!isFullScreen)}>
              {isFullScreen ? t('Close') : t('Expand')}
            </Button>
            {!isReadOnly && !isClient && (
              <Button variant="primary" tooltip={t('Save')} size="small" onClick={(e) => onSave(editorRef.current?.innerHTML || '')}>
                {t('Save')}
              </Button>
            )}
          </div>
        </div>
        {!isReadOnly && !isClient && (
          <div className="flex items-center justify-between">
            <div className="flex gap-2 flex-wrap">
              <Menu as="div" className="relative inline-block text-left">
                <Menu.Button className="p-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                  {currentBlockType}
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-0 mt-2 w-32 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                    <div className="py-1">
                      <Menu.Item disabled={currentBlockType === 'Normal text'}>
                        {({ active }) => (
                          <button
                            onClick={() => handleHeading('p')}
                            className={`${active && currentBlockType !== 'Normal text' ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-200'} ${currentBlockType === 'Normal text' ? 'opacity-50 cursor-not-allowed' : ''} block w-full text-left px-4 py-2 text-sm`}
                            disabled={currentBlockType === 'Normal text'}
                          >
                            Normal text
                          </button>
                        )}
                      </Menu.Item>
                      {[1, 2, 3, 4, 5, 6].map((level) => (
                        <Menu.Item key={level} disabled={currentBlockType === `Header ${level}`}>
                          {({ active }) => (
                            <button
                              onClick={() => handleHeading(level)}
                              className={`${active && currentBlockType !== `Header ${level}` ? 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-gray-100' : 'text-gray-700 dark:text-gray-200'} ${currentBlockType === `Header ${level}` ? 'opacity-50 cursor-not-allowed' : ''} block w-full text-left px-4 py-2 text-sm`}
                              disabled={currentBlockType === `Header ${level}`}
                            >
                              Header {level}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </div>
                  </Menu.Items>
                </Transition>
              </Menu>
              <button
                onClick={() => applyFormat('bold')}
                className={`p-2 ${isBold ? 'bg-gray-200' : ''} text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded`}
              >
                <PiTextBLight />
              </button>
              <button
                onClick={() => applyFormat('italic')}
                className={`p-2 ${isItalic ? 'bg-gray-200' : ''} text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded`}
              >
                <PiTextItalicLight />
              </button>
              <button
                onClick={() => applyFormat('underline')}
                className={`p-2 ${isUnderline ? 'bg-gray-200' : ''} text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded`}
              >
                <PiTextUnderlineLight />
              </button>
              <button
                onClick={() => applyFormat('insertUnorderedList')}
                className={`p-2 ${isUnorderedList ? 'bg-gray-200' : ''} text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded`}
              >
                <PiListBulletsLight />
              </button>
              <button
                onClick={() => applyFormat('insertOrderedList')}
                className={`p-2 ${isOrderedList ? 'bg-gray-200' : ''} text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded`}
              >
                <PiListNumbersLight />
              </button>
            </div>
            {!isClient && contentType !== "caseAnalysis" && versionOptions && (
              <Dropdown options={versionOptions} value={selectedVersion?.toString() || ''} onChange={handleVersionChange} placeholder="Select a version" />
            )}
          </div>
        )}
      </div>
      <div className="overflow-y-auto bg-white dark:bg-black">
        <div
          ref={editorRef}
          contentEditable={!isReadOnly && !isClient && !isFullscreen}
          className={`editor-content flex-1 p-4 text-gray-900 dark:text-gray-100 outline-none prose dark:prose-invert max-w-3xl mx-auto ${isReadOnly || isClient || isFullscreen ? 'cursor-default' : ''}`}  // Add isFullscreen to class for cursor
          style={{ fontFamily: "'Garamond', serif" }}
          onFocus={!isReadOnly && !isClient && !isFullscreen ? handleFocus : undefined}
          onInput={!isReadOnly && !isClient && !isFullscreen ? (e) => {
            debouncedUpdateHistory();
            broadcastContentChange((e.target as HTMLDivElement).innerHTML);
          } : undefined}
        />
      </div>
    </div>
  );

  return (
    <div className="flex bg-background-light h-[calc(100vh-170px)] dark:bg-background-dark custom-scrollbar-main w-full">
      {isFullscreen ? (
        <div className="fixed inset-0 bg-white dark:bg-gray-900 z-50 flex">
          {showOutline && renderOutline(isFullscreen)}
          <div className={`flex-1 ${showOutline ? 'ml-64' : ''}`}>
            {renderEditor(isFullscreen)}
          </div>
        </div>
      ) : (
        <>
          {showOutline && renderOutline(isFullscreen)}
          <div className={`flex-1 ${showOutline ? 'ml-64' : ''}`}>
            {renderEditor(isFullscreen)}
          </div>
        </>
      )}
    </div>
  );
});

export default DynamicEditor;
